import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useEndFlowModal } from '@/contexts/endFlowModalContext';

import {
  claimNpoSendEmailWithFiles,
  claimNpoValidation,
  getLightRatingQuestions,
  getNonProfitOrgById,
  getNonProfitOrgInsights,
  getNonProfitOrgs,
  getNonProfitPaymentMethods,
  getNPOLightRatingQuestionsAnswers,
  nineNintyOcrUpload,
  saveWikiTentativeData,
  searchNpoAISearch,
  searchNpoByName,
  updateLightRatingQuestions,
  updateNonProfitOrg,
  updateNonProfitOrgWiki,
  nonProfitOrgQrCode
} from '../functions/nonProfitOrg';
import { NonProfitOrgParams } from '../models/nonProfitOrgModels';

export const useGetNonProfitOrgs = (params: NonProfitOrgParams) =>
  useInfiniteQuery({
    queryKey: [
      'get-non-profit-orgs',
      params.n,
      params.radio,
      params.religions,
      params.type,
      params.filterByDistance,
      params.latitude,
      params.longitude,
      params.limit,
      params.denominations,
      params.programs,
      params.claimed,
      params.sort_light_rating
    ],
    queryFn: getNonProfitOrgs,
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => {
      if (lastPage.lastN && lastPage.lastLightRaitingScore) {
        return JSON.stringify({ n: lastPage.lastN, lastLightRaitingScore: lastPage.lastLightRaitingScore });
      } else {
        return null;
      }
    }
  });

export const useGetNonProfitById = ({ id }: { id: string }) =>
  useQuery({
    queryKey: ['get-non-profit-by-id', id],
    queryFn: () => getNonProfitOrgById({ id }),
    enabled: !!id
  });

export const useGetNonProfitInsights = ({ id }: { id: string }) =>
  useQuery({
    queryKey: ['get-non-profit-insights', id],
    queryFn: () => getNonProfitOrgInsights({ id }),
    enabled: !!id
  });

export const useUpdateNonProfitOrg = () => {
  const { showError } = useEndFlowModal();
  return useMutation({
    mutationFn: updateNonProfitOrg,
    mutationKey: ['update-non-profit'],
    onError: (e: any) => {
      showError({});
      console.error('Error updating non-profit', e);
    }
  });
};

export const useUpdateNonProfitOrgWiki = () => {
  const { showError } = useEndFlowModal();
  return useMutation({
    mutationFn: updateNonProfitOrgWiki,
    mutationKey: ['update-non-profit-wiki'],
    onError: (e: any) => {
      showError({});
      console.error('Error updating non-profit wiki', e);
    }
  });
};

export const useSaveWikiTentativeData = () => {
  const { showError } = useEndFlowModal();
  return useMutation({
    mutationFn: saveWikiTentativeData,
    mutationKey: ['save-non-profit-wiki-tentative-data'],
    onError: (e: any) => {
      showError({});
      console.error('Error saving non-profit wiki tentative data', e);
    }
  });
};

export const useGetNonProfitLightRatingQuestions = () =>
  useQuery({
    queryKey: ['get-non-profit-light-rating-questions'],
    queryFn: getLightRatingQuestions
  });

export const useGetNPOLightRatingQuestionsAnswers = ({ id }: { id: string }) =>
  useQuery({
    queryKey: ['get-npo-light-rating-questions-answers', id],
    queryFn: () => getNPOLightRatingQuestionsAnswers({ id }),
    enabled: !!id
  });

export const useUpdateNonProfitOrgLightRatingQuestions = () => {
  const { showError } = useEndFlowModal();
  return useMutation({
    mutationFn: updateLightRatingQuestions,
    mutationKey: ['update-non-profit-light-rating-questions'],
    onError: (e: any) => {
      showError({ message: e.message });
      console.error('Error updating non-profit light rating questions', e);
    }
  });
};

export const useSearchNpoByName = () =>
  useMutation({
    mutationKey: ['search-npo-by-name'],
    mutationFn: searchNpoByName
  });

export const useSearchNpoAISearch = () =>
  useMutation({
    mutationKey: ['search-npo-ai-search'],
    mutationFn: searchNpoAISearch
  });

export const useClaimNpoValidation = () => {
  const { showError } = useEndFlowModal();
  return useMutation({
    mutationFn: claimNpoValidation,
    mutationKey: ['claim-npo-validation'],
    onError: (e: any) => {
      showError({});
      console.error('Error claiming npo validation', e);
    }
  });
};

export const useClaimNpoSendEmailWithFiles = () => {
  const { showError } = useEndFlowModal();
  return useMutation({
    mutationFn: claimNpoSendEmailWithFiles,
    mutationKey: ['claim-npo-send-email-files'],
    onError: (e: any) => {
      showError({});
      console.error('Error sending claim npo email files', e);
    }
  });
};

export const useNineNintyUpload = () => {
  const { showError } = useEndFlowModal();
  return useMutation({
    mutationFn: nineNintyOcrUpload,
    mutationKey: ['nine-ninty-ocr-upload'],
    onError: (e: any) => {
      const axiosError = e as AxiosError;
      const errorResponse = axiosError.response as { data: { message?: string } };
      showError({ title: axiosError.message, message: errorResponse?.data.message ?? '' });
      console.error(errorResponse);
    }
  });
};

export const useGetNonProfitPaymentMethods = () =>
  useMutation({
    mutationKey: ['get-non-profit-payment-methods'],
    mutationFn: getNonProfitPaymentMethods
  });

export const useNonProfitOrgQrCode = () => {
  const { showError } = useEndFlowModal();
  return useMutation({
    mutationFn: nonProfitOrgQrCode,
    mutationKey: ['non-profit-org-qr-code'],
    onError: (e: any) => {
      showError({ message: e.message });
      console.error('Error generating non-profit org qr code', e);
    }
  });
};
